import React, { useState, useEffect } from 'react';
import censusApiService from '../services/censusApiService';

const CensusDataExplorer = ({ onDataSelect }) => {
    const [selectedDataset, setSelectedDataset] = useState('acs5');
    const [selectedYear, setSelectedYear] = useState(2022);
    const [selectedCategory] = useState('demographics');
    const [selectedVariables, setSelectedVariables] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiKeyStatus, setApiKeyStatus] = useState('checking');
    const [fetchedData, setFetchedData] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState({});
    
    // Check if API key is configured
    useEffect(() => {
        const apiKey = process.env.REACT_APP_CENSUS_API_KEY;
        console.log('Census API Key status:', apiKey ? 'Found' : 'Not found');
        console.log('Key length:', apiKey ? apiKey.length : 0);
        
        if (!apiKey || apiKey === '') {
            setApiKeyStatus('missing');
        } else {
            setApiKeyStatus('configured');
        }
    }, []);
    
    // Get available datasets and categories
    const datasets = censusApiService.datasets;
    const categories = selectedDataset && datasets[selectedDataset].variables 
        ? Object.keys(datasets[selectedDataset].variables) 
        : [];
    
    // Get variables for selected category
    const getVariablesForCategory = () => {
        if (!selectedDataset || !selectedCategory) return {};
        return datasets[selectedDataset].variables[selectedCategory] || {};
    };
    
    // Toggle category expansion
    const toggleCategory = (category) => {
        setExpandedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };
    
    // Handle variable selection
    const toggleVariable = (variableCode) => {
        setSelectedVariables(prev => {
            if (prev.includes(variableCode)) {
                return prev.filter(v => v !== variableCode);
            } else {
                return [...prev, variableCode];
            }
        });
    };
    
    // Fetch data for selected variables
    const fetchData = async () => {
        if (selectedVariables.length === 0) {
            setError('Please select at least one variable');
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const data = await censusApiService.getStateData(
                selectedDataset,
                selectedYear,
                selectedVariables
            );
            
            setFetchedData(data);
            if (onDataSelect) {
                onDataSelect(data);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
    // Search for variables
    const searchVariables = () => {
        if (!searchTerm) return {};
        return censusApiService.searchVariables(selectedDataset, searchTerm);
    };
    
    // Render API key setup instructions
    const renderApiKeySetup = () => (
        <div style={{
            padding: '20px',
            backgroundColor: '#FFF3CD',
            border: '1px solid #FFC107',
            borderRadius: '8px',
            marginBottom: '20px'
        }}>
            <h4 style={{ color: '#856404', marginBottom: '10px' }}>
                🔑 Census API Key Required
            </h4>
            <p style={{ fontSize: '14px', color: '#856404', marginBottom: '15px' }}>
                To use the Census Data Explorer, you need a free API key from the US Census Bureau.
            </p>
            <ol style={{ fontSize: '14px', color: '#856404', marginLeft: '20px' }}>
                <li>Visit: <a href="https://api.census.gov/data/key_signup.html" target="_blank" rel="noopener noreferrer">
                    https://api.census.gov/data/key_signup.html
                </a></li>
                <li>Enter your email to receive your API key</li>
                <li>Create a file named <code>.env</code> in the project root</li>
                <li>Add: <code>REACT_APP_CENSUS_API_KEY=your_key_here</code></li>
                <li>Restart the development server</li>
            </ol>
        </div>
    );
    
    // Calculate statistics
    const getStatistics = () => {
        let totalVariables = 0;
        Object.values(datasets).forEach(dataset => {
            if (dataset.variables) {
                Object.values(dataset.variables).forEach(category => {
                    totalVariables += Object.keys(category).length;
                });
            }
        });
        return {
            totalDatasets: Object.keys(datasets).length,
            totalVariables,
            selectedVariables: selectedVariables.length
        };
    };
    
    const stats = getStatistics();
    
    return (
        <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
            <h2 style={{
                color: '#1E3A8A',
                marginBottom: '20px',
                borderBottom: '2px solid #DC143C',
                paddingBottom: '10px'
            }}>
                🏛️ US Census Data Explorer
            </h2>
            
            {/* Statistics Bar */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '15px',
                marginBottom: '20px',
                padding: '15px',
                backgroundColor: '#F8F9FA',
                borderRadius: '6px'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1E3A8A' }}>
                        {stats.totalDatasets}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Datasets</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1E3A8A' }}>
                        {stats.totalVariables.toLocaleString()}+
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Variables Available</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>
                        {stats.selectedVariables}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Selected</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: apiKeyStatus === 'configured' ? '#4CAF50' : '#FFC107' 
                    }}>
                        {apiKeyStatus === 'configured' ? '✅' : '⚠️'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>API Status</div>
                </div>
            </div>
            
            {/* API Key Status */}
            {apiKeyStatus === 'missing' && renderApiKeySetup()}
            {apiKeyStatus === 'configured' && (
                <div style={{
                    padding: '10px',
                    backgroundColor: '#D4EDDA',
                    border: '1px solid #C3E6CB',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px'
                }}>
                    <span style={{ fontSize: '20px' }}>✅</span>
                    <span style={{ color: '#155724', fontSize: '14px' }}>
                        Census API key detected! You have access to 600+ variables across all datasets.
                    </span>
                </div>
            )}
            
            {/* Controls */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '20px'
            }}>
                {/* Dataset Selector */}
                <div>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                        Dataset
                    </label>
                    <select
                        value={selectedDataset}
                        onChange={(e) => setSelectedDataset(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                        }}
                    >
                        {Object.entries(datasets).map(([key, dataset]) => (
                            <option key={key} value={key}>
                                {dataset.name}
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* Year Selector */}
                <div>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                        Year
                    </label>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                        }}
                    >
                        {datasets[selectedDataset]?.years.map(year => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* Search */}
                <div>
                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                        Search Variables
                    </label>
                    <input
                        type="text"
                        placeholder="e.g., income, poverty, education..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ddd'
                        }}
                    />
                </div>
                
                {/* Fetch Button */}
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                        onClick={fetchData}
                        disabled={loading || apiKeyStatus !== 'configured'}
                        style={{
                            width: '100%',
                            padding: '8px 16px',
                            backgroundColor: loading ? '#6c757d' : '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'wait' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ? 'Fetching...' : `Fetch ${selectedVariables.length} Variables`}
                    </button>
                </div>
            </div>
            
            {/* Variable Categories */}
            <div style={{
                marginBottom: '20px',
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '6px'
            }}>
                {categories.map(category => {
                    const variables = getVariablesForCategory()[category] || 
                                    (searchTerm ? searchVariables()[category] : {});
                    const isExpanded = expandedCategories[category];
                    const variableCount = Object.keys(variables || {}).length;
                    
                    if (variableCount === 0 && searchTerm) return null;
                    
                    return (
                        <div key={category} style={{
                            borderBottom: '1px solid #e0e0e0'
                        }}>
                            <div
                                onClick={() => toggleCategory(category)}
                                style={{
                                    padding: '12px',
                                    backgroundColor: isExpanded ? '#f8f9fa' : '#fff',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '18px' }}>
                                        {isExpanded ? '📂' : '📁'}
                                    </span>
                                    <span style={{ fontWeight: '500', textTransform: 'capitalize' }}>
                                        {category}
                                    </span>
                                    <span style={{
                                        fontSize: '12px',
                                        padding: '2px 8px',
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        borderRadius: '12px'
                                    }}>
                                        {variableCount} variables
                                    </span>
                                </div>
                                <span>{isExpanded ? '▼' : '▶'}</span>
                            </div>
                            
                            {isExpanded && (
                                <div style={{
                                    padding: '12px',
                                    backgroundColor: '#fafafa',
                                    maxHeight: '300px',
                                    overflowY: 'auto'
                                }}>
                                    {Object.entries(variables || {}).map(([code, description]) => (
                                        <label
                                            key={code}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '6px',
                                                marginBottom: '4px',
                                                backgroundColor: selectedVariables.includes(code) ? '#e8f5e9' : '#fff',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                border: '1px solid',
                                                borderColor: selectedVariables.includes(code) ? '#4CAF50' : '#e0e0e0'
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedVariables.includes(code)}
                                                onChange={() => toggleVariable(code)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            <div style={{ flex: 1 }}>
                                                <code style={{
                                                    fontSize: '11px',
                                                    color: '#666',
                                                    backgroundColor: '#f5f5f5',
                                                    padding: '2px 4px',
                                                    borderRadius: '2px'
                                                }}>
                                                    {code}
                                                </code>
                                                <span style={{
                                                    marginLeft: '8px',
                                                    fontSize: '13px'
                                                }}>
                                                    {description}
                                                </span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            
            {/* Error Display */}
            {error && (
                <div style={{
                    padding: '12px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    color: '#721c24',
                    marginBottom: '20px'
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            
            {/* Data Display */}
            {fetchedData && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '6px',
                    maxHeight: '300px',
                    overflowY: 'auto'
                }}>
                    <h4 style={{ marginBottom: '10px' }}>Fetched Data ({fetchedData.length} records)</h4>
                    <pre style={{
                        fontSize: '12px',
                        backgroundColor: '#fff',
                        padding: '10px',
                        borderRadius: '4px',
                        overflow: 'auto'
                    }}>
                        {JSON.stringify(fetchedData.slice(0, 5), null, 2)}
                        {fetchedData.length > 5 && '\n... and ' + (fetchedData.length - 5) + ' more records'}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default CensusDataExplorer;